// The /chopshop?edit=<url> deep link (DirectorConsole builds it). A link must
// never make the page fetch an arbitrary URL, so only our own API paths, our
// own origin and Cloudinary (where gallery media lives) are accepted.

const CLOUDINARY_PREFIX = "https://res.cloudinary.com/";

// The URL parser drops tabs and newlines anywhere, so "/\n/evil.example" is
// really "//evil.example": strip them before any check.
function clean(url: string): string {
  return String(url ?? "").replace(/[\t\n\r]/g, "").trim();
}

function pageOrigin(): string | null {
  try {
    const origin = typeof location !== "undefined" ? location.origin : "";
    return origin && origin !== "null" ? origin : null;
  } catch {
    return null;
  }
}

export function isAllowedEditUrl(url: string): boolean {
  if (typeof url !== "string") return false;
  const raw = clean(url);
  if (!raw) return false;
  // A plain path on this site. "//host" and "/\host" never start with "/api/".
  if (raw.startsWith("/api/")) return true;
  let parsed: URL;
  try {
    parsed = new URL(raw); // absolute URLs only: a relative string throws here
  } catch {
    return false;
  }
  if (parsed.username || parsed.password) return false;
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
  const origin = pageOrigin();
  if (origin && parsed.origin === origin) return true;
  return raw.startsWith(CLOUDINARY_PREFIX) && parsed.protocol === "https:" && parsed.host === "res.cloudinary.com";
}

function lastSegment(path: string, decode: boolean): string {
  const parts = path.split(/[\\/]/).filter(Boolean);
  const leaf = parts[parts.length - 1] ?? "";
  if (!decode) return leaf.trim();
  try {
    return decodeURIComponent(leaf).trim();
  } catch {
    return leaf.trim();
  }
}

/** A display name for a media URL: the `path` query parameter's last segment when present, else the last path segment. */
export function nameFromUrl(url: string): string {
  try {
    const parsed = new URL(clean(url), "https://page.invalid/");
    // searchParams values are already decoded; the pathname is not.
    const fromParam = parsed.searchParams.get("path");
    const name = fromParam ? lastSegment(fromParam, false) : lastSegment(parsed.pathname, true);
    return name || "media";
  } catch {
    return "media";
  }
}
